/**
 * LinkJobsDock — a floating dock that keeps every Cognara linking run visible.
 *
 * The run lives on the server, so leaving the page no longer kills it: the dock
 * polls the job registry from anywhere in the app, shows the micro schedule,
 * the % bar and the step it is on, and hands the review screen back when the
 * run finishes.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useProject } from '@/contexts/ProjectContext';
import { Button } from '@/components/ui/button';
import { Loader2, X, Sparkles, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

const STATUS_META = {
  RUNNING: { color: '#38BDF8', label: 'Running' },
  COMPLETE: { color: '#10B981', label: 'Complete' },
  FAILED: { color: '#F43F5E', label: 'Failed' },
  CANCELLED: { color: '#94A3B8', label: 'Cancelled' },
};

export default function LinkJobsDock() {
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { activeProjectId } = useProject();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/link-jobs',
        activeProjectId ? { params: { project_id: activeProjectId } } : undefined);
      const all = data.jobs || [];
      // Keep the dock small: everything running, plus the three latest results.
      const running = all.filter(j => j.status === 'RUNNING');
      const finished = all.filter(j => j.status !== 'RUNNING').slice(0, 3);
      setJobs([...running, ...finished]);
    } catch { /* the dock must never break a page */ }
  }, [activeProjectId]);

  useEffect(() => {
    load();
    const running = jobs.some(j => j.status === 'RUNNING');
    const t = setInterval(load, running ? 2500 : 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, jobs.some(j => j.status === 'RUNNING')]);

  const dismiss = async (jobId) => {
    setJobs(prev => prev.filter(j => j.jobId !== jobId));
    try { await api.post(`/link-jobs/${jobId}/dismiss`); } catch { /* already gone */ }
  };
  const cancel = async (jobId) => {
    try { await api.post(`/link-jobs/${jobId}/cancel`); load(); } catch { /* finished already */ }
  };

  useEffect(() => {
    const running = jobs.some(j => j.status === 'RUNNING');
    if (running) setOpen(true);   // a live run should announce itself
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs.some(j => j.status === 'RUNNING')]);

  if (!jobs.length) return null;
  const running = jobs.filter(j => j.status === 'RUNNING').length;

  return (
    <div className="fixed bottom-4 right-4 z-[70] w-[340px] max-w-[92vw] rounded-xl border shadow-2xl backdrop-blur"
         style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}
         data-testid="link-jobs-dock">
      <button className="w-full flex items-center gap-2 px-3 py-2 text-left"
              onClick={() => setOpen(o => !o)} data-testid="link-jobs-toggle">
        <Sparkles className="w-3.5 h-3.5" style={{ color: '#38BDF8' }} />
        <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
          Cognara linking {running ? `· ${running} running` : '· idle'}
        </span>
        {open ? <ChevronDown className="w-3.5 h-3.5 ml-auto" style={{ color: 'var(--text-muted)' }} />
              : <ChevronUp className="w-3.5 h-3.5 ml-auto" style={{ color: 'var(--text-muted)' }} />}
      </button>

      {open && (
        <div className="max-h-[320px] overflow-auto">
          {jobs.map(j => {
            const meta = STATUS_META[j.status] || STATUS_META.RUNNING;
            return (
              <div key={j.jobId} className="px-3 py-2 border-t" style={{ borderColor: 'var(--border-color)' }}
                   data-testid={`link-job-${j.jobId}`}>
                <div className="flex items-center gap-1.5">
                  {j.status === 'RUNNING' ? <Loader2 className="w-3 h-3 animate-spin" style={{ color: meta.color }} />
                    : j.status === 'COMPLETE' ? <CheckCircle2 className="w-3 h-3" style={{ color: meta.color }} />
                    : <AlertTriangle className="w-3 h-3" style={{ color: meta.color }} />}
                  <span className="text-[11px] font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                    {j.label}
                  </span>
                  <span className="text-[10px] ml-auto font-mono" style={{ color: meta.color }}>
                    {j.status === 'RUNNING' ? `${Math.round(j.percent || 0)}%` : meta.label}
                  </span>
                  {j.status !== 'RUNNING' && (
                    <button onClick={() => dismiss(j.jobId)} className="ml-1" title="Dismiss"
                            data-testid={`link-job-dismiss-${j.jobId}`}>
                      <X className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />
                    </button>
                  )}
                </div>

                {j.status === 'RUNNING' && (
                  <div className="h-1 mt-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border-color)' }}>
                    <div className="h-full rounded-full transition-all"
                         style={{ width: `${Math.max(3, Math.round(j.percent || 0))}%`, background: meta.color }}
                         data-testid={`link-job-bar-${j.jobId}`} />
                  </div>
                )}

                <p className="text-[10px] mt-1 truncate" style={{ color: 'var(--text-muted)' }}
                   data-testid={`link-job-phase-${j.jobId}`}>
                  {j.status === 'RUNNING' ? (j.phase || 'Working…')
                    : j.error ? j.error
                    : j.summary ? `${j.summary.total ?? j.summary.itemsAnalysed ?? 0} items · ${j.summary.high ?? j.summary.itemsMatched ?? 0} strong matches`
                    : 'Finished'}
                </p>

                <div className="flex items-center gap-1 mt-1">
                  {j.route && (
                    <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[10px]"
                            onClick={() => navigate(j.route)} data-testid={`link-job-open-${j.jobId}`}>
                      Open review
                    </Button>
                  )}
                  {j.status === 'RUNNING' && (
                    <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[10px] text-rose-500"
                            onClick={() => cancel(j.jobId)} data-testid={`link-job-cancel-${j.jobId}`}>
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
